import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import ShareButtons from "../components/ShareButtons";
import { useLang } from "../components/LangContext";
import { STR } from "../lib/i18n";
import { api, API } from "../lib/api";
import { CATEGORY_FALLBACK_IMG, formatDate } from "../lib/categories";

export default function PostDetail() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = STR[lang];
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/posts/${slug}`, { params: { lang } })
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug, lang]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#F6F5F0]" data-testid="post-not-found">
        <Navbar />
        <div className="mx-auto max-w-3xl px-6 pb-32 pt-48">
          <p className="font-serif text-4xl italic text-[#2D4032]">{t.post.notFound}</p>
          <Link to="/reportage" data-testid="not-found-back-link" className="mt-8 inline-flex items-center gap-2 text-[#9E643E] hover:underline">
            <ArrowLeft className="h-4 w-4" /> {t.post.notFoundBack}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#F6F5F0]" data-testid="post-loading">
        <Navbar />
        <div className="mx-auto max-w-3xl px-6 pt-48">
          <p className="font-serif text-2xl italic text-[#7B8A80]">{t.post.loading}</p>
        </div>
      </div>
    );
  }

  const images = post.images || [];
  const heroImg = images[0] ? `${API}/files/${images[0]}` : CATEGORY_FALLBACK_IMG[post.category];
  const restImages = images.slice(1);

  return (
    <div data-testid="post-detail-page" className="min-h-screen bg-[#F6F5F0]">
      <Navbar />
      <article className="mx-auto max-w-4xl px-6 pb-24 pt-36 md:pt-44">
        <Reveal y={24}>
          <Link
            to="/reportage"
            data-testid="post-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#7B8A80] transition-colors duration-300 hover:text-[#9E643E]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t.post.back}
          </Link>
          <p className="overline-label mt-10 text-[#9E643E]">{t.categories[post.category].label}</p>
          <h1 data-testid="post-title" className="mt-6 font-serif text-4xl font-medium leading-[1.1] tracking-tight text-[#1A221E] sm:text-5xl lg:text-6xl">
            {post.title}
          </h1>
          <p data-testid="post-date" className="mt-6 text-xs tracking-widest text-[#7B8A80]">
            {formatDate(post.created_at, lang)}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 overflow-hidden">
            <img src={heroImg} alt={post.title} data-testid="post-hero-image" className="aspect-[16/10] w-full object-cover" />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="post-body" className="mt-14 space-y-7 text-base leading-loose text-[#1A221E]/85 md:text-lg">
            {post.body.split(/\n+/).filter(Boolean).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </Reveal>

        {restImages.length > 0 && (
          <Reveal delay={0.1}>
            <div data-testid="post-gallery" className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2">
              {restImages.map((img, i) => (
                <div key={img} className="overflow-hidden">
                  <img
                    src={`${API}/files/${img}`}
                    alt={`${post.title} – bild ${i + 2}`}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
                  />
                </div>
              ))}
            </div>
          </Reveal>
        )}

        <Reveal delay={0.1}>
          <div className="mt-20 border-t border-[#D8D3C4] pt-12">
            <ShareButtons post={post} />
          </div>
        </Reveal>
      </article>
      <Footer />
    </div>
  );
}
